import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "../../primitives/tabs";
import { AnimatedTabsProps } from "./types";
import { cn } from "@gettactic/helpers";
import { TabIndicator } from "@cmp-src/tabs/AnimatedTabs/TabsIndicator";

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

/** Tabs whose panels slide in from the side of the tab picked and can be swiped. */
export const AnimatedTabs = React.forwardRef<HTMLDivElement, AnimatedTabsProps>(
  (
    {
      tabs,
      defaultValue,
      tabStyle = "underline",
      className,
      withIndicator = false,
      allowSwipe = true,
      extraAction,
      onValueChange,
      contentRef,
      value
    },
    ref
  ) => {
    const [[activeTab, direction], setActiveTab] = useState([
      defaultValue || tabs[0]?.value,
      0
    ]);
    const [tabIndex, setTabIndex] = useState(
      tabs.findIndex((tab) => tab.value === activeTab)
    );
    const containerRef = useRef<HTMLDivElement>(null);

    const paginate = (newValue: string) => {
      const newIndex = tabs.findIndex((tab) => tab.value === newValue);
      const newDirection = newIndex > tabIndex ? 1 : -1;
      setTabIndex(newIndex);
      setActiveTab([newValue, newDirection]);
    };

    const isControlled = value !== undefined;

    const handlePaginate = (newValue: string) => {
      // Uncontrolled tabs still report the change
      onValueChange?.(newValue);
      if (isControlled) return;

      paginate(newValue);
    };

    React.useEffect(() => {
      if (isControlled) {
        const newIndex = tabs.findIndex((tab) => tab.value === value);
        setTabIndex(newIndex);
        setActiveTab([value, newIndex > tabIndex ? 1 : -1]);
      }
    }, [value, isControlled, tabs]);

    const isSafariMobile =
      typeof navigator !== "undefined" &&
      /iPhone/i.test(navigator.userAgent) &&
      !/Chrome|CriOS|Android/i.test(navigator.userAgent);

    return (
      <Tabs
        value={activeTab}
        onValueChange={handlePaginate}
        className={cn("w-full", className)}
        ref={ref}
      >
        <div ref={containerRef} className="relative flex gap-2">
          <TabsList className="flex items-center justify-between">
            <div className="flex items-center">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className={cn(tab.className, "font-normal")}
                >
                  {tab.icon && <span className="mr-2">{tab.icon}</span>}
                  {tab.label}
                </TabsTrigger>
              ))}
              {withIndicator && (
                <TabIndicator
                  tabs={tabs}
                  activeTab={activeTab}
                  tabStyle={tabStyle}
                  containerRef={containerRef}
                />
              )}
            </div>
          </TabsList>
          {extraAction}
        </div>

        <div className="relative mt-4 h-full overflow-hidden" ref={contentRef}>
          <AnimatePresence initial={false} mode="popLayout" custom={direction}>
            {tabs.map((tab) => {
              if (activeTab !== tab.value) return;

              const content = (
                <TabsContent
                  value={tab.value}
                  forceMount
                  className={cn("h-full", tab.className)}
                >
                  {tab.content}
                </TabsContent>
              );

              if (isSafariMobile) {
                return (
                  <div
                    key={tab.value}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%"
                    }}
                  >
                    {content}
                  </div>
                );
              }

              return (
                <motion.div
                  key={tab.value}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  drag={allowSwipe ? "x" : undefined}
                  dragConstraints={
                    allowSwipe ? { left: 0, right: 0 } : undefined
                  }
                  dragElastic={allowSwipe ? 1 : undefined}
                  onDragEnd={
                    allowSwipe
                      ? (_, { offset, velocity }) => {
                          const swipe = swipePower(offset.x, velocity.x);

                          if (swipe < -swipeConfidenceThreshold) {
                            const nextIndex = tabIndex + 1;
                            if (nextIndex < tabs.length) {
                              handlePaginate(tabs[nextIndex].value);
                            }
                          } else if (swipe > swipeConfidenceThreshold) {
                            const prevIndex = tabIndex - 1;
                            if (prevIndex >= 0) {
                              handlePaginate(tabs[prevIndex].value);
                            }
                          }
                        }
                      : undefined
                  }
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%"
                  }}
                >
                  {content}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </Tabs>
    );
  }
);
